// clientStorageService.ts
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL, FirebaseStorage } from 'firebase/storage';

interface ClientFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

class ClientStorageService {
  private clientApp: FirebaseApp | null = null;
  private clientStorage: FirebaseStorage | null = null;
  private isInitialized = false;

  /**
   * Initialize the client's Firebase app for storage
   */
  async initializeClientStorage(config: ClientFirebaseConfig): Promise<void> {
    try {
      // Create a secondary Firebase app instance for the client
      this.clientApp = initializeApp(config, 'client-storage-app');
      this.clientStorage = getStorage(this.clientApp);
      this.isInitialized = true;
      console.log('Client storage initialized successfully');
    } catch (error) {
      console.error('Failed to initialize client storage:', error);
      throw new Error('Failed to connect to client storage');
    }
  }

  /**
   * Check if the service is properly initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.clientStorage !== null;
  }

  /**
   * Upload a file to the client's Firebase Storage
   */
  async uploadFile(
    file: File, 
    folder: string = 'editor-uploads',
    onProgress?: (progress: number) => void
  ): Promise<string> {
    if (!this.isReady()) {
      throw new Error('Client storage not initialized');
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size exceeds 10MB limit');
    }

    try {
      // Generate a unique filename
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedName}`;
      const filePath = `${folder}/${fileName}`;

      // Create a reference to the file location
      const storageRef = ref(this.clientStorage!, filePath);

      // Upload the file
      const uploadResult = await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error('Failed to upload file');
    }
  }

  /**
   * Get file extension and validate it's an allowed type
   */
  validateFileType(file: File, allowedTypes?: string[]): boolean {
    if (!allowedTypes) {
      // Default allowed types for editor content
      allowedTypes = [
        'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', // Images
        'pdf', 'doc', 'docx', 'txt', 'rtf', // Documents
        'mp4', 'webm', 'ogg', 'mp3', 'wav', // Media
        'zip', 'rar' // Archives
      ];
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    return fileExtension ? allowedTypes.includes(fileExtension) : false;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Cleanup the client app (call this when component unmounts)
   */
  cleanup(): void {
    if (this.clientApp) {
      // Note: Firebase doesn't provide a direct way to delete an app instance
      // But setting references to null helps with garbage collection
      this.clientApp = null;
      this.clientStorage = null;
      this.isInitialized = false;
    }
  }
}

// Export a singleton instance
export const clientStorageService = new ClientStorageService();
export type { ClientFirebaseConfig };